import IconButton from 'Components/Buttons/IconButton/index.js';
import Panel from 'Components/Panel';
import { actions as readingActions } from 'ducks/deviceVerificationReadings';
import { actions as logActions } from 'ducks/logs.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import icons from 'utils/ui/icons.js';
import useVerificationScreen from '../useVerificationScreen';
import { buildMeterValues, formatRealtimeValue, getMeasurementFromReadingKindName, getMeasurementRatio, getRealtimeValue, isFilled, liveValue, numericValues } from '../utils';
import VerificationShell, { PanelButton, PanelHeader } from '../VerificationShell';
import CombinedTable from './CombinedTable';
import TechnicalReport from './TechnicalReport';

const AbsoluteVerification = ({ device }) => {
    const resetForm = () => {
        setTechnicianValues(emptyTechnicianValues);
        setSystemValues(emptySystemValues);
        setManualValues(emptyManualValues);
        setTechnicianComments('');
    };

    const screen = useVerificationScreen(device, resetForm);
    const { currentVerification, dispatch, drawerOpen, finalValidated, image, loading, measurements, profile } = screen;
    const { readingKindNames, readings, readingsById, realtime, submitting, technicianValidated, unitSymbols, verificationNumber, verificationReadings, verificationsIndex } = screen;
    const { setDrawerOpen, setFinalComments, setFinalStatus, setImage, setShowCameraModal } = screen;

    const { photo } = profile;
    const photoRequired = photo === 'required';

    const storedReading = verificationReadings[0];
    const storedImage = storedReading?.image || null;

    // A stored reading draws its rows from its own keys, in the API's order; the form from the
    // live profile, where a single quantity has no auxiliaries to name.
    const { auxiliary = [], primary } = useMemo(() => {
        if (!storedReading) {
            return profile;
        }
        const technicianKinds = Object.keys(storedReading.technician_values);
        return { auxiliary: Object.keys(storedReading.meter_values).filter(kind => !technicianKinds.includes(kind)), primary: technicianKinds };
    }, [profile, storedReading]);
    const emptyTechnicianValues = useMemo(() => primary.reduce((acc, kind) => ({ ...acc, [kind]: '' }), {}), [primary]);
    // A kind the equipment does not read starts as not applicable.
    const emptySystemValues = useMemo(() => primary.reduce((acc, kind) => ({ ...acc, [kind]: readingKindNames.includes(kind) ? '' : null }), {}), [primary, readingKindNames]);
    const emptyManualValues = useMemo(() => auxiliary.reduce((acc, kind) => ({ ...acc, [kind]: readingKindNames.includes(kind) ? '' : null }), {}), [auxiliary, readingKindNames]);
    const otherMeasurements = useMemo(() => {
        const kinds = [...primary, ...auxiliary];
        return measurements.filter(measurement => !kinds.includes(readingsById[measurement.reading_id]?.reading_kind_name));
    }, [auxiliary, measurements, primary, readingsById]);

    const [technicianValues, setTechnicianValues] = useState(emptyTechnicianValues);
    const [systemValues, setSystemValues] = useState(emptySystemValues);
    const [auxiliaryValues, setAuxiliaryValues] = useState({});
    const [manualValues, setManualValues] = useState(emptyManualValues);
    const [technicianComments, setTechnicianComments] = useState('');

    const onlineKinds = useMemo(() => primary.filter(kind => liveValue(realtime, getMeasurementFromReadingKindName(kind, measurements, readings))), [measurements, primary, readings, realtime]);

    const technicianCanValidate = primary.every(kind => isFilled(systemValues[kind]) && isFilled(technicianValues[kind])) && auxiliary.every(kind => isFilled(auxiliaryValues[kind]));

    const handleTechnicianValidation = () => {
        if (!currentVerification) {
            return;
        }
        const meterValues = buildMeterValues(systemValues, auxiliaryValues, primary, auxiliary);
        dispatch(readingActions.create({
            comments: technicianComments,
            device_verification_id: currentVerification.id,
            meter_values: numericValues(meterValues),
            online: realtime.length > 0,
            technician_values: numericValues(technicianValues),
            ...(image ? { image_data_uri: image } : {})
        }, () => {}));
    };

    const canSubmit = Boolean(currentVerification) && technicianCanValidate && (!photoRequired || Boolean(image));

    // A declared not-applicable survives a reset; the rest starts over.
    const resetSystemValues = () => setSystemValues(prev => Object.fromEntries(primary.map(kind => [kind, prev[kind] === null ? null : emptySystemValues[kind]])));

    const handleRefresh = () => {
        resetSystemValues();
        dispatch(logActions.fetchRealtime(device.id));
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        if (!technicianValidated) {
            resetSystemValues();
        }
    };
    const handleTechnicianChange = label => event => {
        const value = event.target.value;
        setTechnicianValues(prev => ({ ...prev, [label]: value }));
    };
    const handleSystemChange = label => event => {
        const value = event.target.value;
        setSystemValues(prev => ({ ...prev, [label]: value }));
    };
    const handleManualChange = kind => event => {
        const value = event.target.value;
        setManualValues(prev => ({ ...prev, [kind]: value }));
    };
    const notApplicableChange = setValues => kind => event => {
        setValues(prev => ({ ...prev, [kind]: event.target.checked ? null : '' }));
    };
    const handleManualNotApplicableChange = notApplicableChange(setManualValues);
    const handleSystemNotApplicableChange = notApplicableChange(setSystemValues);

    useEffect(() => {
        if (drawerOpen && !technicianValidated && device.id) {
            dispatch(logActions.fetchRealtime(device.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerOpen]);

    useEffect(() => {
        if (!device.id || verificationsIndex === null) {
            return;
        }
        // The selected verification may still be in flight — keep the local form state until it lands.
        if (!currentVerification) {
            return;
        }
        if (technicianValidated) {
            const newSys = {};
            const newTech = {};
            primary.forEach(k => {
                newSys[k] = storedReading.meter_values[k];
                newTech[k] = storedReading.technician_values[k];
            });
            setSystemValues(newSys);
            setTechnicianValues(newTech);
        } else {
            setTechnicianValues(emptyTechnicianValues);
            setSystemValues(emptySystemValues);
        }
        setManualValues(emptyManualValues);
        setTechnicianComments(storedReading?.comments || '');
        setFinalStatus(currentVerification?.final_status === 'no_status' ? '' : (currentVerification?.final_status || ''));
        setFinalComments(currentVerification?.final_comments || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [device.id, verificationsIndex, technicianValidated, verificationReadings.length]);

    useEffect(() => {
        setImage(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentVerification?.id]);

    // Clearing any earlier would wipe a capture taken before its reading existed.
    const hadStoredImage = useRef(false);
    useEffect(() => {
        if (storedImage && !hadStoredImage.current) {
            setImage(null);
        }
        hadStoredImage.current = Boolean(storedImage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storedImage]);

    useEffect(() => {
        const values = {};
        auxiliary.forEach(kind => {
            if (technicianValidated) {
                values[kind] = storedReading.meter_values[kind];
                return;
            }
            const measurement = getMeasurementFromReadingKindName(kind, measurements, readings);
            const match = liveValue(realtime, measurement);
            if (match) {
                values[kind] = formatRealtimeValue(match.value, getMeasurementRatio(measurement, readingsById));
            } else if (isFilled(manualValues[kind])) {
                values[kind] = manualValues[kind];
            }
        });
        setAuxiliaryValues(values);
    }, [auxiliary, manualValues, measurements, readings, readingsById, realtime, storedReading, technicianValidated]);

    useEffect(() => {
        if (realtime.length === 0 || technicianValidated) {
            return;
        }
        setSystemValues(prev => {
            const next = { ...prev };
            // A declared not-applicable stays until it is unchecked.
            primary.forEach(kind => {
                const measurement = getMeasurementFromReadingKindName(kind, measurements, readings);
                if (next[kind] === null || !liveValue(realtime, measurement)) {
                    return;
                }
                next[kind] = getRealtimeValue(realtime, measurement, readingsById);
            });
            return next;
        });
        // The new verification's reset may land after the realtime response — the fill has to run again then.
    }, [currentVerification?.id, measurements, primary, readings, readingsById, realtime, technicianValidated]);

    // While collecting the panel is where comments and the photo are added; once the
    // reading is stored it only earns its place with something to show.
    const technicianPanelVisible = !technicianValidated || Boolean(storedReading?.comments) || Boolean(storedImage);
    const extraTabs = technicianPanelVisible ? [{ text: 'Technician Comments and Photos', id: 'technician-comments' }] : [];

    const validateAction = !technicianValidated && !finalValidated && (
        <span style={{ paddingRight: 20 }}><PanelButton disabled={!canSubmit || submitting} onClick={handleTechnicianValidation}>Validate</PanelButton></span>
    );

    return (
        <VerificationShell device={device} finalReportReady={technicianValidated} onDrawerClose={handleDrawerClose} primaryTab={{ text: 'Verification', id: 'verification' }} screen={screen} tabs={extraTabs}>
            <div id="verification" style={{ margin: '8px 0' }}>
                <Panel
                    header={
                        <PanelHeader action={validateAction}>
                            {`Verification #${verificationNumber}`}
                            {!technicianValidated && (
                                <IconButton icon={icons.refresh} onClick={handleRefresh} spin={loading} tooltip="Refresh values"/>
                            )}
                        </PanelHeader>
                    }
                >
                    <CombinedTable
                        auxiliary={auxiliary}
                        device={device}
                        loading={(loading || submitting) && !technicianValidated}
                        manualValues={manualValues}
                        measurements={measurements}
                        onlineKinds={onlineKinds}
                        onManualChange={handleManualChange}
                        onManualNotApplicableChange={handleManualNotApplicableChange}
                        onSystemChange={handleSystemChange}
                        onSystemNotApplicableChange={handleSystemNotApplicableChange}
                        onTechnicianChange={handleTechnicianChange}
                        otherMeasurements={otherMeasurements}
                        primary={primary}
                        readings={readings}
                        readingsById={readingsById}
                        realtime={realtime}
                        storedMeterValues={storedReading?.meter_values}
                        systemValues={systemValues}
                        technicianValues={technicianValues}
                        unitSymbols={unitSymbols}
                        validated={technicianValidated}
                    />
                </Panel>
            </div>
            {technicianPanelVisible && (
                <div id="technician-comments" style={{ margin: '8px 0' }}>
                    <Panel
                        header={
                            <PanelHeader>
                                {`Technician Comments and Photos #${verificationNumber}`}
                                {!technicianValidated && !finalValidated && (
                                    <IconButton icon={['fas', 'camera']} onClick={() => setShowCameraModal(true)} tooltip="Add photo"/>
                                )}
                            </PanelHeader>
                        }
                    >
                        <TechnicalReport
                            image={storedImage || image}
                            onDiscardImage={() => setImage(null)}
                            onTechnicianCommentsChange={setTechnicianComments}
                            technicianComments={technicianComments}
                            validated={technicianValidated}
                        />
                    </Panel>
                </div>
            )}
        </VerificationShell>
    );
};

export default AbsoluteVerification;
